const template = document.createElement("template");
template.innerHTML = `
<style>
  :host{
    display: inline-block;
    font-family: var(--ctl-font-sans, system-ui, -apple-system, Segoe UI, Roboto, Arial);
  }

  button{
    all: unset;
    box-sizing: border-box;
    display: inline-flex;
    align-items: center;
    gap: var(--ctl-space-2, 8px);
    cursor: pointer;
    user-select: none;
    padding: var(--ctl-space-2, 8px) var(--ctl-space-2-5, 10px);
    border-radius: var(--ctl-radius-10, 10px);
    color: var(--ctl-color-muted, #64748b);
    font-weight: var(--ctl-font-weight-bold, 700);
    font-size: var(--ctl-font-size-md, 13px);
    line-height: 1;
    white-space: nowrap;
    transition: color var(--ctl-transition-fast, 120ms ease),
                background var(--ctl-transition-fast, 120ms ease),
                box-shadow var(--ctl-transition-fast, 120ms ease);
  }

  button:hover {
    color: var(--ctl-color-text, #1e293b);
    background: color-mix(in srgb, var(--ctl-color-text, #1e293b) 6%, transparent);
  }

  button:focus-visible{
    outline: none;
    box-shadow: var(--ctl-focus-ring, 0 0 0 3px rgba(99, 102, 241, 0.25));
  }

  :host([aria-selected="true"]) button{
    color: var(--ctl-color-text, #1e293b);
  }

  /* Underline variant */
  :host([data-variant="underline"]) button{
    border-radius: var(--ctl-radius-10, 10px) var(--ctl-radius-10, 10px) 0 0;
  }
  :host([data-variant="underline"][aria-selected="true"]) button{
    box-shadow: inset 0 calc(-1 * var(--ctl-border-width-thick, 2px)) 0 var(--ctl-color-brand, #6366f1);
  }
  :host([data-variant="underline"][aria-selected="true"]) button:hover{
    background: color-mix(in srgb, var(--ctl-color-brand, #6366f1) 8%, transparent);
  }

  /* Pill variant */
  :host([data-variant="pill"]) button{
    border-radius: var(--ctl-radius-md, 8px);
    padding: var(--ctl-space-2, 8px) var(--ctl-space-3, 12px);
  }
  :host([data-variant="pill"][aria-selected="true"]) button{
    background: color-mix(in srgb, var(--ctl-color-brand, #6366f1) 12%, transparent);
    border: var(--ctl-border-width-thin, 1px) solid color-mix(in srgb, var(--ctl-color-brand, #6366f1) 25%, var(--ctl-color-border, #e2e8f0));
  }
  :host([data-variant="pill"][aria-selected="true"]) button:hover{
    background: color-mix(in srgb, var(--ctl-color-brand, #6366f1) 18%, transparent);
  }

  :host([disabled]) button{
    opacity: var(--ctl-opacity-disabled, 0.5);
    cursor: not-allowed;
  }
</style>

<button id="btn" type="button">
  <slot></slot>
</button>
`;

export class CtlTab extends HTMLElement {
  static get observedAttributes(){
    return ["value", "disabled", "aria-selected"];
  }

  constructor(){
    super();
    this.attachShadow({ mode: "open" }).appendChild(template.content.cloneNode(true));
    this._btn = this.shadowRoot.getElementById("btn");
  }

  connectedCallback(){
    if (!this.hasAttribute("role")) this.setAttribute("role", "tab");
    this._sync();
  }

  attributeChangedCallback(){
    this._sync();
  }

  get value(){ return this.getAttribute("value") || ""; }
  set value(v){ this.setAttribute("value", String(v ?? "")); }

  get disabled(){ return this.hasAttribute("disabled"); }
  set disabled(v){ v ? this.setAttribute("disabled","") : this.removeAttribute("disabled"); }

  set variant(v){
    const val = String(v || "underline");
    this.setAttribute("data-variant", val);
  }

  _sync(){
    this._btn.setAttribute("aria-disabled", this.disabled ? "true" : "false");
    this._btn.tabIndex = this.tabIndex; // keep host tabIndex mirrored via ctl-tabs
  }

  focus(){ this._btn.focus(); }
  click(){ this._btn.click(); }
}

customElements.define("ctl-tab", CtlTab);
